/**
 * Branded confirmation dialog — a drop-in, on-brand replacement for the
 * native window.confirm().
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Delete this script?",
 *     description: "This can't be undone.",
 *     confirmLabel: "Delete",
 *     tone: "danger",
 *   });
 *   if (!ok) return;
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, Trash2, HelpCircle } from "lucide-react";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [options, setOptions] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOptions(opts);
    });
  }, []);

  const resolve = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setOptions(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmModal options={options} onResolve={resolve} />
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within a ConfirmProvider");
  return ctx;
}

function ConfirmModal({ options, onResolve }) {
  const open = !!options;
  const tone = options?.tone || "default";

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onResolve(false);
      if (e.key === "Enter") onResolve(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onResolve]);

  const Icon = tone === "danger" ? Trash2 : tone === "warning" ? AlertTriangle : HelpCircle;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
          data-testid="confirm-dialog-overlay"
        >
          <div
            className="absolute inset-0 bg-[#1c1c1c]/45 backdrop-blur-[3px]"
            onClick={() => onResolve(false)}
          />
          <motion.div
            role="alertdialog"
            aria-modal="true"
            className="relative w-full max-w-[420px] bg-[var(--paper)] border border-[var(--line)] rounded-xl shadow-2xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.94, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            data-testid="confirm-dialog"
          >
            {/* top accent bar */}
            <div
              className="h-1 w-full"
              style={{ background: tone === "danger" ? "var(--err)" : "var(--brand)" }}
            />
            <div className="p-6">
              <div className="flex items-start gap-4">
                <span
                  className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center"
                  style={{
                    background: tone === "danger" ? "rgba(185,74,72,0.12)" : "var(--brand-soft)",
                    color: tone === "danger" ? "var(--err)" : "var(--brand)",
                  }}
                >
                  <Icon size={20} strokeWidth={1.75} />
                </span>
                <div className="flex-1 min-w-0 pt-0.5">
                  <h2 className="font-display text-xl text-[var(--ink-primary)] leading-snug" data-testid="confirm-dialog-title">
                    {options?.title || "Are you sure?"}
                  </h2>
                  {options?.description && (
                    <p className="text-sm text-[var(--ink-secondary)] mt-1.5 leading-relaxed" data-testid="confirm-dialog-description">
                      {options.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2.5 mt-7">
                <button
                  onClick={() => onResolve(false)}
                  className="px-4 py-2 text-sm rounded-md text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)] transition-colors"
                  data-testid="confirm-dialog-cancel"
                >
                  {options?.cancelLabel || "Cancel"}
                </button>
                <button
                  onClick={() => onResolve(true)}
                  autoFocus
                  className="px-4 py-2 text-sm rounded-md text-white font-medium transition-transform active:scale-[0.98]"
                  style={{ background: tone === "danger" ? "var(--err)" : "var(--brand)" }}
                  data-testid="confirm-dialog-confirm"
                >
                  {options?.confirmLabel || "Confirm"}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
